import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Layout } from "@/components/Layout";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Package, Calendar, DollarSign, TrendingUp, FileCheck, Wrench, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { fi } from "date-fns/locale";
import { getCustomerDisplayName, formatCurrency } from "@/lib/formatters";
import { ORDER_STATUS_LABELS } from "@/config/constants";
import { Badge } from "@/components/ui/badge";
import { OrderDialog } from "@/components/orders/OrderDialog";
import { QuotaWarningsAlert } from "@/components/dashboard/QuotaWarningsAlert";
import { useOrderStatusMutation } from "@/hooks/useOrderStatusMutation";
import { queryKeys } from "@/lib/query-keys";

type OrderStatusCount = {
  status: string;
  count: number;
};

type UpcomingInstallation = {
  id: string;
  scheduled_date: string;
  task_type: string;
  order_id: string;
  order: {
    id: string;
    order_number: string;
    customer: {
      first_name: string | null;
      last_name: string | null;
      company_name: string | null;
    };
    cemetery: {
      name: string;
    };
  };
};

type PartnerCommission = {
  partner_id: string;
  partner_name: string;
  total_unpaid: number;
};

type WorkshopOrder = {
  id: string;
  order_number: string;
  order_date: string;
  customer: {
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
  };
  pickup_completed_date: string | null;
};

// Tyyppi provisiotilausriville
interface CommissionOrderRow {
  partner_id: string;
  commission_amount: string | number;
  partners: { name: string } | null;
}

// Status labels are now imported from @/config/constants

export default function Dashboard() {
  const { role } = useAuth();
   const queryClient = useQueryClient();
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

   // Mark workshop order as ready for installation - now uses centralized hook with email notification
   const markReadyMutation = useOrderStatusMutation({
     onSuccess: () => {
      toast.success("Työ merkitty valmiiksi palautukseen");
    },
  });

  // Hae tilausten määrät tiloittain
  const { data: orderStats, isLoading: loadingOrders } = useQuery({
    queryKey: queryKeys.orders.stats(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("status")
        .is("deleted_at", null);

      if (error) throw error;

      // Laske määrät per status
      const statusCounts: Record<string, number> = {};
      data.forEach((order) => {
        statusCounts[order.status] = (statusCounts[order.status] || 0) + 1;
      });

      return Object.entries(statusCounts).map(([status, count]) => ({
        status,
        count,
      })) as OrderStatusCount[];
    },
    enabled: role === "admin",
  });

  // Hae tulevat asennukset (seuraavat 14 päivää)
  const { data: upcomingInstallations, isLoading: loadingInstallations } = useQuery({
    queryKey: queryKeys.installations.upcoming(),
    queryFn: async () => {
      const today = new Date().toISOString().split("T")[0];
      const twoWeeksLater = addDays(new Date(), 14).toISOString().split("T")[0];

      const { data, error } = await supabase
        .from("installations")
        .select(`
          id,
          scheduled_date,
          task_type,
          order_id,
          order:orders (
            id,
            order_number,
            customer:customers (
              first_name,
              last_name,
              company_name
            ),
            cemetery:cemeteries (
              name
            )
          )
        `)
        .gte("scheduled_date", today)
        .lte("scheduled_date", twoWeeksLater)
        .is("deleted_at", null)
        .neq("status", "completed")
        .neq("status", "cancelled")
        .order("scheduled_date", { ascending: true });

      if (error) throw error;
      return data as unknown as UpcomingInstallation[];
    },
    enabled: role === "admin",
  });

  // Hae maksamattomat provisiot kumppaneittain
  const { data: unpaidCommissions, isLoading: loadingCommissions } = useQuery({
    queryKey: ["unpaid-commissions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(`
          partner_id,
          commission_amount,
          partners (
            name
          )
        `)
        .not("partner_id", "is", null)
        .not("commission_amount", "is", null)
        .gt("commission_amount", 0)
        .is("deleted_at", null);

      if (error) throw error;

      // Ryhmittele kumppaneittain (oletetaan että kaikki ovat maksamattomia tässä vaiheessa)
      const commissionsByPartner: Record<string, { name: string; total: number }> = {};
      
      data.forEach((order: CommissionOrderRow) => {
        const partnerId = order.partner_id;
        const partnerName = order.partners?.name || "Tuntematon kumppani";
        const rawAmount = order.commission_amount;
        const amount = typeof rawAmount === "number" ? rawAmount : parseFloat(rawAmount) || 0;

        if (!commissionsByPartner[partnerId]) {
          commissionsByPartner[partnerId] = { name: partnerName, total: 0 };
        }
        commissionsByPartner[partnerId].total += amount;
      });

      return Object.entries(commissionsByPartner).map(([partner_id, { name, total }]) => ({
        partner_id,
        partner_name: name,
        total_unpaid: total,
      })) as PartnerCommission[];
    },
    enabled: role === "admin",
  });

  // Hae odottavat vedoshyväksynnät
  const { data: pendingProofs, isLoading: loadingProofs } = useQuery({
    queryKey: ["pending-proofs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("order_proofs")
        .select("id, status")
        .in("status", ["sent", "revision_requested"]);
      
      if (error) throw error;
      return data || [];
    },
    enabled: role === "admin",
  });

  // Hae pajalla olevat työt (pickup_work tilaukset joiden nouto on tehty)
  const { data: workshopOrders, isLoading: loadingWorkshop } = useQuery({
    queryKey: queryKeys.workshop.all,
    queryFn: async () => {
      // Hae pickup_work tilaukset joilla on valmis nouto-tehtävä
      const { data: orders, error } = await supabase
        .from("orders")
        .select(`
          id,
          order_number,
          order_date,
          customer:customers (
            first_name,
            last_name,
            company_name
          )
        `)
        .eq("order_type", "pickup_work")
        .eq("status", "confirmed")
        .is("deleted_at", null)
        .order("order_date", { ascending: true });

      if (error) throw error;

      if (!orders || orders.length === 0) return [];

      // Batch fetch pickup installations - eliminates N+1 queries
      const orderIds = orders.map(o => o.id);
      const { data: pickupInstallations } = await supabase
        .from("installations")
        .select("order_id, completed_date")
        .in("order_id", orderIds)
        .eq("task_type", "pickup")
        .eq("status", "completed")
        .is("deleted_at", null);

      // Create a map of order_id -> completed_date
      const pickupMap: Record<string, string | null> = {};
      pickupInstallations?.forEach(inst => {
        pickupMap[inst.order_id] = inst.completed_date;
      });

      // Filter and merge - only include orders with completed pickups
      return orders
        .filter(order => pickupMap[order.id] !== undefined)
        .map(order => ({
          ...order,
          pickup_completed_date: pickupMap[order.id],
        })) as WorkshopOrder[];
    },
    enabled: role === "admin",
  });

  // Use centralized getCustomerDisplayName from formatters

  const totalOrders = orderStats?.reduce((sum, stat) => sum + stat.count, 0) || 0;

  if (role === "installer") {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Etusivu</h1>
            <p className="text-muted-foreground mt-2">
              Tervetuloa hautakivi-hallintajärjestelmään
            </p>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Omat asennukset</CardTitle>
              <CardDescription>Sinulle osoitetut tehtävät</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Siirry "Omat asennukset" -sivulle nähdäksesi tehtäväsi
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (role === "partner") {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Etusivu</h1>
            <p className="text-muted-foreground mt-2">
              Tervetuloa kumppaniportaaliin
            </p>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Kumppanin näkymä</CardTitle>
              <CardDescription>Omat tilaukset ja provisiot</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Kumppanin ominaisuudet tulossa pian.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Check for query errors to avoid infinite spinner
  const queryResults = [
    { loading: loadingOrders, error: orderStats === undefined && !loadingOrders },
    { loading: loadingInstallations, error: upcomingInstallations === undefined && !loadingInstallations },
    { loading: loadingCommissions, error: unpaidCommissions === undefined && !loadingCommissions },
    { loading: loadingProofs, error: pendingProofs === undefined && !loadingProofs },
    { loading: loadingWorkshop, error: workshopOrders === undefined && !loadingWorkshop },
  ];
  const isAnyLoading = queryResults.some(q => q.loading);
  const hasAnyError = queryResults.some(q => q.error);

  if (hasAnyError && !isAnyLoading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <p className="text-muted-foreground">Tietojen lataus epäonnistui</p>
          <Button onClick={() => window.location.reload()}>
            Yritä uudelleen
          </Button>
        </div>
      </Layout>
    );
  }

  if (isAnyLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const pendingProofCount = pendingProofs?.length || 0;
  const revisionRequestedCount = pendingProofs?.filter(p => p.status === "revision_requested").length || 0;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Yleiskatsaus tilaustilanteeseen
          </p>
        </div>

        {/* API-kiintiövaroitukset */}
        <QuotaWarningsAlert />

        {/* Tilausten määrät tiloittain */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Tilaukset tiloittain</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Yhteensä</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalOrders}</div>
                <p className="text-xs text-muted-foreground">Kaikki tilaukset</p>
              </CardContent>
            </Card>
            {orderStats?.map((stat) => (
              <Card key={stat.status}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {ORDER_STATUS_LABELS[stat.status] || stat.status}
                  </CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.count}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Odottavat vedoshyväksynnät */}
          {pendingProofCount > 0 && (
            <Card className="border-orange-500/50 bg-orange-500/5">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Vedoshyväksynnät</CardTitle>
                <FileCheck className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{pendingProofCount}</div>
                <p className="text-xs text-muted-foreground">
                  Odottaa asiakkaan vastausta
                  {revisionRequestedCount > 0 && ` (${revisionRequestedCount} muutospyyntöä)`}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Pajalla olevat työt */}
        {workshopOrders && workshopOrders.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4">
              Pajalla olevat työt
            </h2>
            <Card className="border-amber-500/50 bg-amber-500/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-amber-600" />
                  {workshopOrders.length} työtä pajalla
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {workshopOrders.slice(0, 5).map((order) => {
                    const customerName = order.customer?.company_name 
                      || `${order.customer?.first_name || ""} ${order.customer?.last_name || ""}`.trim() 
                      || "Tuntematon";
                    return (
                      <div
                        key={order.id}
                        className="flex items-center justify-between gap-3 border-b pb-3 last:border-0"
                      >
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => setSelectedOrderId(order.id)}
                            className="font-medium text-primary hover:underline text-left"
                          >
                            #{order.order_number}
                          </button>
                          <p className="text-sm text-muted-foreground truncate">{customerName}</p>
                          {order.pickup_completed_date && (
                            <p className="text-xs text-muted-foreground">
                              Noudettu {format(new Date(order.pickup_completed_date), "d.M.", { locale: fi })}
                            </p>
                          )}
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              className="gap-1 shrink-0 bg-green-600 hover:bg-green-700"
                              disabled={markReadyMutation.isPending}
                            >
                              {markReadyMutation.isPending ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <CheckCircle2 className="h-3 w-3" />
                              )}
                              Valmis
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Merkitäänkö työ valmiiksi?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tilaus #{order.order_number} ({customerName}) merkitään valmiiksi palautusasennukseen.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Peruuta</AlertDialogCancel>
                              <AlertDialogAction
                                 onClick={() => markReadyMutation.mutate({ 
                                   orderId: order.id, 
                                   newStatus: "ready_for_installation",
                                   oldStatus: "confirmed" 
                                 })}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Merkitse valmiiksi
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    );
                  })}
                  {workshopOrders.length > 5 && (
                    <p className="text-sm text-muted-foreground text-center pt-2">
                      + {workshopOrders.length - 5} muuta työtä
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tulevat asennukset */}
        <div>
          <h2 className="text-xl font-semibold mb-4">
            Tulevat asennukset (14 päivää)
          </h2>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {upcomingInstallations?.length || 0} asennusta tulossa
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!upcomingInstallations || upcomingInstallations.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Ei tulevia asennuksia seuraavien 14 päivän aikana
                </p>
              ) : (
                <div className="space-y-3">
                  {upcomingInstallations.slice(0, 5).map((installation) => (
                    <div
                      key={installation.id}
                      className="flex items-center justify-between border-b pb-3 last:border-0"
                    >
                      <div className="flex-1">
                        <button
                          onClick={() => setSelectedOrderId(installation.order_id)}
                          className="font-medium text-primary hover:underline text-left"
                        >
                          #{installation.order.order_number}
                        </button>
                        <p className="text-sm text-muted-foreground">
                          {getCustomerDisplayName(installation.order.customer)} -{" "}
                          {installation.order.cemetery.name}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">
                          {format(
                            new Date(installation.scheduled_date),
                            "d.M.yyyy",
                            { locale: fi }
                          )}
                        </p>
                        <Badge variant="outline" className="text-xs mt-1">
                          {installation.task_type}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {upcomingInstallations.length > 5 && (
                    <p className="text-sm text-muted-foreground text-center pt-2">
                      + {upcomingInstallations.length - 5} muuta asennusta
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Maksamattomat provisiot */}
        <div>
          <h2 className="text-xl font-semibold mb-4">
            Maksamattomat provisiot
          </h2>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Kumppaneiden provisiot
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!unpaidCommissions || unpaidCommissions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Ei maksamattomia provisioita
                </p>
              ) : (
                <div className="space-y-3">
                  {unpaidCommissions.map((commission) => (
                    <div
                      key={commission.partner_id}
                      className="flex items-center justify-between border-b pb-3 last:border-0"
                    >
                      <p className="font-medium">{commission.partner_name}</p>
                      <p className="text-lg font-bold">
                        {commission.total_unpaid.toFixed(2)} €
                      </p>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-3 border-t-2">
                    <p className="font-bold">Yhteensä</p>
                    <p className="text-xl font-bold">
                      {unpaidCommissions
                        .reduce((sum, c) => sum + c.total_unpaid, 0)
                        .toFixed(2)}{" "}
                      €
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {selectedOrderId && (
        <OrderDialog
          open={!!selectedOrderId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedOrderId(null);
              // Invalidate queries when dialog closes to refresh data
              queryClient.invalidateQueries({ queryKey: queryKeys.workshop.all });
              queryClient.invalidateQueries({ queryKey: queryKeys.installations.upcoming() });
              queryClient.invalidateQueries({ queryKey: queryKeys.orders.stats() });
            }
          }}
          orderId={selectedOrderId}
        />
      )}
    </Layout>
  );
}
